import { useCallback, useEffect, useRef, useState } from 'react';
import { getActiveJob } from '../services/projects';
import type { JobStatusMessage } from '../models/pipelineEvents';
import { usePipelineEvents } from '../context/PipelineEventsContext';

export type OrganizeJobState =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'arranged'; versionId: string }
  | { phase: 'failed'; message: string | null };

export interface OrganizeJob {
  state: OrganizeJobState;
  /** The server accepted a request to arrange the values. */
  queued: () => void;
  dismiss: () => void;
  /** Reads the project's active job again, for an outcome whose event may have been missed. */
  recheck: () => void;
}

const ArrangedMs = 5000;
const Idle: OrganizeJobState = { phase: 'idle' };

/**
 * The project's organizing job as the creator follows it: running from the moment it is queued until its layout
 * is stored, arranged for a few seconds after that, and failed until dismissed or the next run. Needs the page's
 * `PipelineEventsProvider` for `projectId`.
 */
export function useOrganizeJob(projectId: string): OrganizeJob {
  const [state, setState] = useState<OrganizeJobState>(Idle);
  const projectIdRef = useRef(projectId);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const read = useCallback(() => {
    getActiveJob(projectId)
      .then((job) => {
        if (projectIdRef.current !== projectId) return;
        const organizing = job?.jobType === 'Organize';
        setState((previous) => {
          if (organizing) return previous.phase === 'idle' ? { phase: 'running' } : previous;
          return previous.phase === 'running' ? Idle : previous;
        });
      })
      .catch(() => undefined);
  }, [projectId]);

  useEffect(() => read(), [read]);

  useEffect(() => {
    if (state.phase !== 'arranged') return;
    const timer = window.setTimeout(() => setState(Idle), ArrangedMs);
    return () => window.clearTimeout(timer);
  }, [state]);

  const onJobStatus = useCallback((message: JobStatusMessage) => {
    if (message.jobType !== 'Organize') return;
    if (message.status === 'Processing') setState({ phase: 'running' });
    if (message.status === 'Failed') setState({ phase: 'failed', message: message.failureMessage });
    if (message.status === 'Cancelled') setState(Idle);
    if (message.status === 'Completed') setState((previous) => (previous.phase === 'running' ? Idle : previous));
  }, []);

  usePipelineEvents({
    onReconnected: read,
    onJobStatus,
    onLayoutReady: (message) => setState({ phase: 'arranged', versionId: message.versionId }),
  });

  const queued = useCallback(() => setState({ phase: 'running' }), []);
  const dismiss = useCallback(() => setState(Idle), []);

  return { state, queued, dismiss, recheck: read };
}
